import type { JobApplication } from '../types'

export interface CalendarEventOptions {
  title: string
  startDateTime: Date
  endDateTime: Date
  description: string
  location?: string
  reminderMinutesBefore?: number
}

/**
 * Formats a Date to an ICS-compatible UTC timestamp string: YYYYMMDDTHHMMSSZ
 */
function formatICSDateTime(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Generates an ICS file content string for a calendar event.
 * Follows the iCalendar RFC 5545 specification.
 */
export function generateICSContent(options: CalendarEventOptions): string {
  const uid = `${crypto.randomUUID()}@jobflow.app`
  const dtStamp = formatICSDateTime(new Date())
  const dtStart = formatICSDateTime(options.startDateTime)
  const dtEnd = formatICSDateTime(options.endDateTime)
  const reminderMinutes = options.reminderMinutesBefore ?? 15

  // Escape special characters for ICS format
  const escapeICS = (text: string) =>
    text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//JobFlow//Interview Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escapeICS(options.title)}`,
    `DESCRIPTION:${escapeICS(options.description)}`,
    options.location ? `LOCATION:${escapeICS(options.location)}` : '',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT' + reminderMinutes + 'M',
    'ACTION:DISPLAY',
    `DESCRIPTION:Reminder: ${escapeICS(options.title)} starts in ${reminderMinutes} minutes`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]

  return lines.filter(Boolean).join('\r\n')
}

/**
 * Triggers a download of an .ics file.
 */
export function downloadICSFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Builds a Google Calendar event URL from CalendarEventOptions.
 * This opens Google Calendar in a new tab with pre-filled event details.
 */
export function buildGoogleCalendarUrl(options: CalendarEventOptions): string {
  const formatGCALDate = (date: Date) =>
    date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: options.title,
    dates: `${formatGCALDate(options.startDateTime)}/${formatGCALDate(options.endDateTime)}`,
    details: options.description,
  })

  if (options.location) {
    params.set('location', options.location)
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/**
 * Creates calendar event options from a JobApplication's interview data.
 * Uses the nextAction.dueDate as the interview date/time and includes
 * relevant job details in the event description.
 */
export function createInterviewEventOptions(application: JobApplication): CalendarEventOptions | null {
  if (!application.nextAction || application.nextAction.type !== 'interview' || !application.nextAction.dueDate) {
    return null
  }

  const dueDate = new Date(application.nextAction.dueDate)
  // Default interview to 1 hour, starting at 10 AM on the due date if no time specified
  const startDateTime = new Date(dueDate)
  startDateTime.setHours(10, 0, 0, 0)

  const endDateTime = new Date(startDateTime)
  endDateTime.setHours(startDateTime.getHours() + 1)

  const descriptionParts = [
    `Interview for: ${application.position} at ${application.company}`,
  ]

  if (application.nextAction.description) {
    descriptionParts.push('', application.nextAction.description)
  }

  if (application.notes) {
    descriptionParts.push('', '--- Job Notes ---', application.notes)
  }

  if (application.sourceUrl) {
    descriptionParts.push('', `Job listing: ${application.sourceUrl}`)
  }

  if (application.contacts.length > 0) {
    const contactInfo = application.contacts
      .map(c => `${c.name}${c.role ? ` (${c.role})` : ''}${c.email ? ` - ${c.email}` : ''}${c.phone ? ` - ${c.phone}` : ''}`)
      .join('\n')
    descriptionParts.push('', '--- Contacts ---', contactInfo)
  }

  return {
    title: `Interview: ${application.position} at ${application.company}`,
    startDateTime,
    endDateTime,
    description: descriptionParts.join('\n'),
    reminderMinutesBefore: 15,
  }
}
